import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { Pencil, Plus, ShieldX } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FiscalProfileFormDialog } from './FiscalProfileFormDialog';
import {
  CUSTOMER_FISCAL_PROFILES_QUERY,
  DEACTIVATE_FISCAL_PROFILE_MUTATION,
} from './invoicing.gql';
import type { FiscalProfile } from './types';

import { TotemMark } from '@/shared/brand/TotemLogo';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';

interface ProfilesResponse {
  customerFiscalProfiles: FiscalProfile[];
}

interface Props {
  customer_id: string;
}

export function CustomerFiscalProfilesTab({ customer_id }: Props): JSX.Element {
  const { t } = useTranslation();
  const { data, refetch, loading } = useQuery<ProfilesResponse>(
    CUSTOMER_FISCAL_PROFILES_QUERY,
    {
      variables: { customer_id },
      fetchPolicy: 'cache-and-network',
    },
  );
  const [show_form, setShowForm] = useState<{ mode: 'create' } | { mode: 'edit'; profile: FiscalProfile } | null>(
    null,
  );
  const [error_text, setErrorText] = useState<string | null>(null);
  const [deactivate] = useMutation(DEACTIVATE_FISCAL_PROFILE_MUTATION);

  const profiles = data?.customerFiscalProfiles ?? [];

  const onClickDeactivate = async (profile: FiscalProfile): Promise<void> => {
    setErrorText(null);
    try {
      await deactivate({ variables: { input: { id: profile.id } } });
      await refetch();
    } catch (err) {
      if (err instanceof ApolloError) {
        setErrorText(err.graphQLErrors[0]?.message ?? err.message);
      } else if (err instanceof Error) {
        setErrorText(err.message);
      }
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-lg font-bold tracking-tight">
            {t('invoicing.fiscal_profiles_title')}
          </h3>
          <p className="text-xs text-muted-foreground">
            {t('invoicing.fiscal_profiles_subtitle')}
          </p>
        </div>
        <Button type="button" size="sm" onClick={() => setShowForm({ mode: 'create' })}>
          <Plus className="h-4 w-4" aria-hidden />
          <span>{t('invoicing.add_fiscal_profile')}</span>
        </Button>
      </div>

      {error_text ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error_text}
        </div>
      ) : null}

      {loading && profiles.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2">
        {profiles.length === 0 && !loading ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 p-4 text-center text-sm text-muted-foreground">
              <TotemMark className="h-10 w-auto text-muted-foreground/40" aria-hidden />
              {t('invoicing.no_fiscal_profiles')}
            </CardContent>
          </Card>
        ) : null}
        {profiles.map((p) => (
          <Card key={p.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-base">{p.legal_name}</CardTitle>
                <div className="flex gap-1">
                  {p.is_default ? <Badge>{t('invoicing.default_badge')}</Badge> : null}
                  {p.status === 'inactive' ? (
                    <Badge variant="outline">{t('invoicing.inactive_badge')}</Badge>
                  ) : null}
                </div>
              </div>
              <p className="font-mono text-xs text-muted-foreground">{p.rfc}</p>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              {p.label ? <p>{p.label}</p> : null}
              <p>
                <span className="text-muted-foreground">
                  {t('invoicing.field_tax_regime')}:{' '}
                </span>
                {p.tax_regime_code}
              </p>
              <p>
                <span className="text-muted-foreground">{t('invoicing.field_postal_code')}: </span>
                {p.postal_code}
              </p>
              <p>
                <span className="text-muted-foreground">{t('invoicing.field_cfdi_usage')}: </span>
                {p.cfdi_usage}
              </p>
              {p.email ? (
                <p>
                  <span className="text-muted-foreground">{t('invoicing.field_email')}: </span>
                  {p.email}
                </p>
              ) : null}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowForm({ mode: 'edit', profile: p })}
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                  <span>{t('common.edit')}</span>
                </Button>
                {p.status === 'active' ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      void onClickDeactivate(p);
                    }}
                  >
                    <ShieldX className="h-4 w-4" aria-hidden />
                    <span>{t('invoicing.deactivate')}</span>
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {show_form ? (
        <FiscalProfileFormDialog
          customer_id={customer_id}
          profile={show_form.mode === 'edit' ? show_form.profile : null}
          on_close={() => setShowForm(null)}
          on_done={async () => {
            setShowForm(null);
            await refetch();
          }}
          on_error={setErrorText}
        />
      ) : null}
    </div>
  );
}
